import { BadgeCheck, LogOut, Smartphone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError, endpoints, type MicrosoftAccount } from '../../services/api';
import { isLicensed, startMicrosoft, useSession } from '../../store/session';
import { PairWithPhone } from '../PairWithPhone';
import { PayOnPhone } from '../PayOnPhone';
import { buttonClass, ghostButtonClass, inputClass, Section } from './controls';

export async function startCheckout(): Promise<string | null> {
  try {
    const { url } = await endpoints.checkout();
    window.location.assign(url);
    return null;
  } catch (e) {
    return e instanceof ApiError && e.code === 'not_configured'
      ? 'Payments are not switched on yet.'
      : e instanceof Error
        ? e.message
        : 'Checkout failed';
  }
}

export function AccountTab() {
  const { config, user, signOut, microsoft, refreshMicrosoft } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pairing, setPairing] = useState(false);

  const accounts = config?.features.accounts ?? false;
  const billing = config?.features.billing ?? false;

  const buy = async () => {
    setBusy(true);
    setError(await startCheckout());
    setBusy(false);
  };

  if (!accounts) {
    return (
      <Section title="Account">
        <p className="muted text-sm">
          Accounts aren’t switched on for this server yet. Everything works on this screen without one; signing in will add
          syncing between screens, cloud photo libraries and ad-free licences.
        </p>
      </Section>
    );
  }

  if (user) {
    const licensed = isLicensed(user);
    return (
      <>
        <Section title="Signed in">
          <p className="text-sm">{user.email}</p>
          <p className="muted text-xs">Settings and lists sync to every screen signed in to this account.</p>
          {user.admin && (
            <a href="/admin" className="text-sm text-[var(--accent)] hover:underline">
              Manage house ads →
            </a>
          )}
          <button className={ghostButtonClass} onClick={() => void signOut()}>
            <LogOut className="h-4 w-4" aria-hidden /> Sign out
          </button>
        </Section>
        <DeleteAccount onDeleted={() => void signOut()} />
        {config?.features.microsoft && (
          <Section
            title="Microsoft accounts"
            description="OneDrive photos, Outlook calendars and To Do lists. Connect your work and personal accounts together to show them all at once."
          >
            <LinkProblem />
            {(microsoft?.accounts ?? []).length > 0 && (
              <ul className="space-y-2">
                {microsoft!.accounts!.map((a) => (
                  <MicrosoftAccountRow key={a.id} account={a} onChanged={() => void refreshMicrosoft()} />
                ))}
              </ul>
            )}
            <button
              className={(microsoft?.accounts ?? []).length ? ghostButtonClass : buttonClass}
              onClick={() => startMicrosoft('link')}
            >
              <MicrosoftLogo /> {(microsoft?.accounts ?? []).length ? 'Connect another Microsoft account' : 'Connect Microsoft account'}
            </button>
            {(microsoft?.accounts ?? []).length > 0 && (
              <p className="muted text-xs">Microsoft asks which account to use: pick “Use another account” to add one that isn’t listed.</p>
            )}
          </Section>
        )}
        <Section title="Licence">
          {licensed ? (
            <p className="flex items-center gap-2 text-sm">
              <BadgeCheck className="h-5 w-5 text-[var(--accent)]" aria-hidden /> Lifetime licence – ad-free on every screen.
            </p>
          ) : (
            <>
              <p className="text-sm">
                KCL Canvas is free, with an occasional short ad. A one-off <strong>£10 lifetime licence</strong> (plus local tax, shown at checkout) removes ads on every
                screen you sign in to.
              </p>
              {billing && <PayOnPhone />}
              <button className={billing ? ghostButtonClass : buttonClass} onClick={() => void buy()} disabled={busy || !billing}>
                {billing ? 'Or pay on this device – £10 + tax' : 'Licences coming soon'}
              </button>
            </>
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}
        </Section>
      </>
    );
  }

  return (
    <>
      <Section title="Sign in with your phone" description="Easiest on a TV: scan a code and approve on your phone.">
        {pairing ? (
          <PairWithPhone onCancel={() => setPairing(false)} />
        ) : (
          <button className={buttonClass} onClick={() => setPairing(true)}>
            <Smartphone className="h-4 w-4" aria-hidden /> Show sign-in code
          </button>
        )}
      </Section>
      <SignInForm />
    </>
  );
}

/** Email/password (+ Microsoft) sign-in. `returnTo` is where a Microsoft sign-in lands afterwards. */
export function SignInForm({ returnTo }: { returnTo?: string }) {
  const { config, setUser } = useSession();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = mode === 'login' ? await endpoints.login(email, password) : await endpoints.register(email, password);
      setUser(res.user);
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title={mode === 'login' ? 'Sign in' : 'Create an account'}
      description={
        config?.features.microsoft
          ? 'Google, Apple and Amazon sign-in are coming soon.'
          : 'Sign in with Microsoft, Google, Apple or Amazon is coming soon.'
      }
    >
      {config?.features.microsoft && (
        <>
          <button
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border)] bg-white px-3 py-2.5 text-sm font-medium text-[#1b1b1b] hover:bg-white/90"
            onClick={() => startMicrosoft('signin', returnTo)}
          >
            <MicrosoftLogo /> Continue with Microsoft
          </button>
          <div className="muted flex items-center gap-3 text-xs">
            <span className="h-px flex-1 bg-[var(--border)]" /> or with email <span className="h-px flex-1 bg-[var(--border)]" />
          </div>
        </>
      )}
      <form onSubmit={submit} className="space-y-3">
        <input
          className={inputClass}
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-label="Email"
        />
        <input
          className={inputClass}
          type="password"
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          placeholder={mode === 'login' ? 'Password' : 'Password (at least 10 characters)'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={mode === 'register' ? 10 : undefined}
          required
          aria-label="Password"
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button className={`${buttonClass} w-full`} disabled={busy} type="submit">
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </button>
      </form>
      <button
        className="muted text-sm underline-offset-2 hover:underline"
        onClick={() => {
          setMode(mode === 'login' ? 'register' : 'login');
          setError(null);
        }}
      >
        {mode === 'login' ? 'New here? Create an account' : 'Already have an account? Sign in'}
      </button>
      <p className="muted text-xs">
        By continuing you agree to the{' '}
        <a href="/terms" className="underline">
          Terms
        </a>{' '}
        and{' '}
        <a href="/privacy" className="underline">
          Privacy Policy
        </a>
        .
      </p>
    </Section>
  );
}

/** A Microsoft account that couldn't be connected because another KCL Canvas account has it. */
function LinkProblem() {
  const problem = useSession((s) => s.linkProblem);
  const clear = useSession((s) => s.setLinkProblem);
  if (!problem) return null;
  const who = problem.email || 'That Microsoft account';
  return (
    <div className="space-y-2 rounded-lg border border-amber-400/60 bg-amber-400/10 p-3 text-sm" role="alert">
      {problem.code === 'linked_elsewhere' ? (
        <>
          <p>
            <strong>{who}</strong> is already connected to another KCL Canvas account (perhaps one you signed in to with it before).
          </p>
          <p className="muted text-xs">
            Move it here to show its calendars, lists and photos on this account. It will be disconnected from the other account.
          </p>
          <div className="flex flex-wrap gap-2">
            <button className={buttonClass} onClick={() => startMicrosoft('link', undefined, false, undefined, { hint: problem.email })}>
              <MicrosoftLogo /> Move it to this account
            </button>
            <button className={ghostButtonClass} onClick={() => clear(null)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p>
            <strong>{who}</strong> is the only way to sign in to your other KCL Canvas account, so moving it would lock that account.
          </p>
          <p className="muted text-xs">
            If you don’t need that account: sign out, sign in with Microsoft using {who}, delete it (Settings → Account → Delete
            account), then sign back in here and connect it again.
          </p>
          <button className={ghostButtonClass} onClick={() => clear(null)}>
            OK
          </button>
        </>
      )}
    </div>
  );
}

/** One connected Microsoft account: type, extra work access, reconnect and disconnect. */
function MicrosoftAccountRow({ account: a, onChanged }: { account: MicrosoftAccount; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <li className="space-y-2 rounded-lg border border-[var(--border)] p-3 text-sm">
      <div className="flex items-center gap-3">
        <MicrosoftLogo />
        <span className="min-w-0 flex-1 truncate">{a.email ?? 'Microsoft account'}</span>
        {a.connected && <span className="muted shrink-0 rounded-full border border-[var(--border)] px-2 py-0.5 text-xs">{a.work ? 'Work' : 'Personal'}</span>}
        <button
          className={ghostButtonClass}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await endpoints.microsoftDisconnect(a.id).catch(() => undefined);
            onChanged();
          }}
        >
          Disconnect
        </button>
      </div>
      {!a.connected ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-amber-400">Microsoft needs you to sign in to this account again.</span>
          <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
            Reconnect
          </button>
        </div>
      ) : a.work && !a.workAccess ? (
        <div className="flex items-center justify-between gap-3">
          <span className="muted text-xs">Allow access to use SharePoint libraries, folders shared with you and shared calendars too.</span>
          <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link', undefined, true, a.id)}>
            Allow SharePoint and shared items
          </button>
        </div>
      ) : a.work ? (
        <p className="muted text-xs">SharePoint libraries, shared folders, shared calendars and Planner are available.</p>
      ) : null}
    </li>
  );
}

/** The four-square Microsoft logo, as required by Microsoft's sign-in branding guidelines. */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/** Permanently deletes the account and everything stored for it on our servers. */
function DeleteAccount({ onDeleted }: { onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await endpoints.deleteAccount();
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the account');
      setBusy(false);
    }
  };
  return (
    <Section title="Delete account">
      {confirming ? (
        <>
          <p className="text-sm">
            This permanently deletes your account, its settings and lists, and its Microsoft connection from our servers. A lifetime
            licence can’t be restored afterwards. This can’t be undone.
          </p>
          <div className="flex gap-2">
            <button className={ghostButtonClass} onClick={() => setConfirming(false)} disabled={busy}>
              Keep my account
            </button>
            <button className={`${buttonClass} !bg-red-600 !text-white`} onClick={() => void remove()} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </>
      ) : (
        <button className={ghostButtonClass} onClick={() => setConfirming(true)}>
          Delete account…
        </button>
      )}
      {error && <p className="text-sm text-red-400">{error}</p>}
    </Section>
  );
}
